import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, forkJoin, merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, skip, switchMap, take, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { SendbirdChatService } from 'src/app/layouts/sendbird-chat/sendbird-chat.service';
import { DividendVipService } from 'src/app/pages/dividend-vip/dividend-vip.service';
import { MiniGameService } from 'src/app/pages/minigame/minigame.service';
import { WalletService } from 'src/app/pages/wallet/wallet.service';
import { GameApi } from 'src/app/shared/apis/game.api';
import { LangModel } from 'src/app/shared/interfaces/country.interface';
import {
  GameRoutesInfo,
  HeaderMenu,
  HomeLabelSceneData,
  LabelScenesRedirectMethodEnum,
  LeftMenu,
} from 'src/app/shared/interfaces/game.interface';
import { CountryUtilsService } from 'src/app/shared/service/country.service';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { NotificationDialogService } from './../../pages/notification-center/dialog/notification-dialog.service';
import { LeftMenuService } from './left-menu.service';

@UntilDestroy()
@Component({
  selector: 'app-left-menu',
  templateUrl: './left-menu.component.html',
  styleUrls: ['./left-menu.component.scss'],
  animations: [
    trigger('show', [
      state(
        'true',
        style({ transform: 'translate3d(0,0,0)', opacity: 1, visibility: 'visible', 'pointer-events': 'all' }),
      ),
      state(
        'false',
        style({ transform: 'translate3d(-100%, 0, 0)', opacity: 0, visibility: 'hidden', 'pointer-events': 'none' }),
      ),
      transition('false => true', [
        animate(
          '400ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ transform: 'translate3d(0,0,0)', opacity: 1, visibility: 'visible' }),
        ),
      ]),
      transition('true => false', [
        animate(
          '400ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ transform: 'translate3d(-100%, 0, 0)', opacity: 0, visibility: 'hidden' }),
        ),
      ]),
    ]),
    trigger('h5-show', [
      state('true', style({ transform: 'translate3d(0,0,0)', opacity: 1, visibility: 'visible' })),
      state('false', style({ transform: 'translate3d(0, 100%, 0)', opacity: 0, visibility: 'hidden' })),
      transition('false => true', [
        animate(
          '200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ transform: 'translate3d(0,0,0)', opacity: 1, visibility: 'visible' }),
        ),
      ]),
      transition('true => false', [
        animate(
          '200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ transform: 'translate3d(0, 100%, 0)', opacity: 0, visibility: 'hidden' }),
        ),
      ]),
    ]),
  ],
})
export class LeftMenuComponent implements OnInit {
  domain = window.location.origin;

  logined!: boolean; //是否已登录
  isH5!: boolean; //是否h5
  allLangData!: LangModel[];

  pageSeries!: string;
  headerMenu!: any;
  leftMenu: LeftMenu[] = [];
  /** 默认左菜单数据 */
  defaultMenuData: Record<string, any>[] = JSON.parse(JSON.stringify(this.leftMenuService.DEFAULT_MENU));
  menuData = JSON.parse(JSON.stringify(this.leftMenuService.LEFT_MENU_DATA)); // 基本菜单数据
  menuReference: any = this.findMenuItem(this.menuData.list, [
    'sports',
    'lottery',
    'lottery-favorites',
    'lottery-history',
    'casino',
    'casino-favorites',
    'casino-history',
    'chess',
    'onLines',
    'langSelect',
    // 'oddsFormat',
    // 'countrySelect'
  ]); //菜单对象引用
  mainReference: any = this.findMenuItem(this.menuData.main, ['sport-main', 'casino-main', 'lottery-main']); //顶部菜单对象引用

  positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      panelClass: 'left-menu-options-overlay-down',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      panelClass: 'left-menu-options-overlay-up',
    },
  ];

  railPositions: ConnectedPosition[] = [...this.positions].reverse();
  railPositionsCenter: ConnectedPosition[] = this.railPositions.map(x => ({
    ...x,
    originX: 'center',
    overlayX: 'center',
  }));

  state!: boolean;
  invisible?: boolean;
  transition!: 'start' | 'end';

  closed!: boolean;
  opened!: boolean;
  processingCompleted!: boolean;

  private menuSync$ = new Subject<boolean>();

  constructor(
    private leftMenuService: LeftMenuService,
    private layout: LayoutService,
    public appService: AppService,
    private countryUtils: CountryUtilsService,
    public miniGameService: MiniGameService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private sendbirdChatService: SendbirdChatService,
    public walletService: WalletService,
    public notificationDialogService: NotificationDialogService,
    private gameApi: GameApi,
    private dividendVipService: DividendVipService,
  ) {}

  ngOnInit() {
    merge(
      // 初始化時立刻反映當前路由
      this.menuSync$.pipe(take(1)),
      // 防抖，避免主執行序卡住，影響動畫
      this.menuSync$.pipe(skip(1), debounceTime(200)),
    )
      .pipe(
        untilDestroyed(this),
        filter(canCheck => canCheck),
      )
      .subscribe(() => this.checkRouteToExpandMenu());

    combineLatest([
      this.layout.leftMenuState$, //伸展或收缩
      this.layout.leftMenuTransition$, //动画状态
      this.layout.leftMenuInvisible$.pipe(
        tap(x => {
          if (x) this.closeTooltip(this.menuData.list);
        }),
      ), //是否不可见
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([state, transition, invisible]) => {
        this.state = state;
        this.invisible = invisible;
        this.opened = state && transition === 'end';
        this.closed = !state && transition === 'end';

        if (!state && this.isH5) {
          this.collapseAllDropdowns();
        }
      });

    this.logined = Boolean(this.localStorageService.loginToken);
    if (this.logined) {
      // 初始获取小游戏收藏
      this.miniGameService.getFavoriteList().subscribe();
      // 初始获取小游戏最近玩过
      this.miniGameService.getRecentLyPlayedGame().subscribe();

      // 設定最新優惠數量
      this.dividendVipService.activityCount$.pipe(untilDestroyed(this)).subscribe(num => {
        const promotionsIndex = this.defaultMenuData.findIndex(x => x.ident === 'latest-offers');
        if (promotionsIndex === -1) return;

        if (this.defaultMenuData[promotionsIndex]) {
          this.defaultMenuData[promotionsIndex].hasBadge = !!num;
          this.defaultMenuData[promotionsIndex].num = of(num);
        }
      });
      this.dividendVipService.getActivityCount();
    }

    //订阅是否是h5
    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));

    //获取所有语言
    this.appService.languages$.pipe(untilDestroyed(this)).subscribe(x => {
      this.allLangData = x;
      this.langInit();
    });

    //盘口类型变化订阅
    // this.appService.oddsType$.pipe(untilDestroyed(this)).subscribe(v => {
    //   this.menuReference['oddsFormat'].select = v;
    // })

    //投注模式 亚洲版与欧洲版 订阅
    // this.appService.betMode$.pipe(untilDestroyed(this)).subscribe(v => {
    //   this.menuReference['countrySelect'].select = v;
    // })

    // 設定通知顯示
    this.appService.noticeCounts$.pipe(untilDestroyed(this)).subscribe(e => {
      const notificationsIndex = this.defaultMenuData.findIndex(x => x.ident === 'notifications');
      if (notificationsIndex === -1) return;

      if (this.defaultMenuData[notificationsIndex]) {
        this.defaultMenuData[notificationsIndex].hasBadge = !!e?.All;
      }
    });

    this.miniGameService.homeScenesSub$
      .pipe(
        untilDestroyed(this),
        map((data: HomeLabelSceneData) => {
          this.miniGameService.allScenesData = data;
          this.headerMenu = data.headerMenus;
          this.leftMenu = data?.leftMenus?.map(x => {
            if (x?.enableFavorites && x.name === 'Casino') {
              const favArr = this.leftMenuService.getFavArr(
                this.miniGameService.miniGameFavoriteNumber$,
                this.miniGameService.miniGameRecentLyPlayedNumber$,
              );
              x.infoExpandList = this.upsertByIdent(x.infoExpandList ?? [], favArr);
            }
            return { ...x, expand: false };
          });
        }),
        switchMap(() =>
          forkJoin([
            this.resolveAllFriendlyUrls(this.leftMenu),
            this.resolveAllFriendlyUrls(this.defaultMenuData as any[]),
          ]),
        ),
        // 订阅小游戏 收藏游戏 最近玩过 数量变化
        // concatMap(_ =>
        //   combineLatest([
        //     this.miniGameService.miniGameFavoriteNumber$,
        //     this.miniGameService.miniGameRecentLyPlayedNumber$,
        //   ]),
        // ),
        // debounceTime(300),
        // map(([favNum, playedNum]) => {
        //   // 这里的近期玩过 是前端统计数量，有几个游戏就会执行几次 ，增加防抖机制只取最后一次
        //   const favArr = this.leftMenuService.getFavArr(
        //     favNum ?? this.miniGameService.miniGameFavoriteNumber$.getValue(),
        //     playedNum ?? this.miniGameService.miniGameRecentLyPlayedNumber$.getValue(),
        //   );
        //   console.log(favArr);
        //   this.leftMenu?.map(x => {
        //     if (x?.enableFavorites) {
        //       // x?.infoExpandList?.splice(0, 2);
        //       // x.infoExpandList = favArr.concat(x.infoExpandList);
        //       // 防抖还是不保险 ，只更新num 不需要全部赋值S
        //       const favoritesIndex = x.infoExpandList.findIndex(item => item.ident === 'casino-favorites');
        //       const historyiIndex = x.infoExpandList.findIndex(item => item.ident === 'casino-history');
        //       if (favoritesIndex === -1 || historyiIndex === -1) {
        //         x.infoExpandList = favArr.concat(x.infoExpandList);
        //       } else {
        //         x.infoExpandList[favoritesIndex].num = favArr[0].num;
        //         x.infoExpandList[historyiIndex].num = favArr[1].num;
        //       }
        //       console.log(x.infoExpandList);
        //     }
        //   });
        // }),
        mergeMap(() => this.layout.page$),
      )
      .subscribe(() => {
        this.menuSync$.next(true);

        // h5时候页面变化恒定关闭菜单
        if (this.isH5 && this.state) this.turn(false);
        if (this.isH5 && this.sendbirdChatService.displayChat$.value) {
          this.sendbirdChatService.hideChat();
        }
      });
  }

  /**
   * 將單一 menu item 的原始路徑解析成美化後的 URL，並寫入 `activeRoutePath`。
   *
   * - `casino/home` / `casino/category`：從 `gameLabelUrlRoutes$` 查 labelURL
   * - `casino/games` / `play`：呼叫 API `getGameRoutes` 查美化路徑；若已是美化路由（無 segment2）則直接使用
   * - 其他：原始路徑直接使用
   *
   * @param menu 要解析的 menu item，解析結果會直接寫入其 `activeRoutePath`
   * @returns 寫入 `activeRoutePath` 後的 menu item
   */
  private resolveFriendlyUrl<T extends { infoExpandList?: T[]; activeRoutePath: string }>(menu: T): Observable<T> {
    const link = this.miniGameService.getLinkByMethod(menu).split('/').slice(1).join('/');
    if (link.startsWith('casino/home') || link.startsWith('casino/category')) {
      return this.appService.gameLabelUrlRoutes$.pipe(
        take(1),
        map(routes => {
          const parent = link.split('/')[1];
          const labelCode = link.split('/')[2];
          const target = routes.find(x => x.labelCode === labelCode);
          menu.activeRoutePath = [parent, target?.labelURL].filter(s => !!s).join('/');
          return menu;
        }),
      );
    }

    if (link.startsWith('casino/games') || link.startsWith('play')) {
      const isGames = link.startsWith('casino/games');
      const parent = link.split('/')[isGames ? 1 : 0];
      const segment1 = link.split('/')[isGames ? 2 : 1];
      const segment2 = link.split('/')[isGames ? 3 : 2];

      // 如果是新的(美化後)路由，不用轉換
      if (!segment2) {
        menu.activeRoutePath = link;
        return of(menu);
      }

      return this.gameApi.getGameRoutes({ providerCatId: segment1, gameId: segment2 }).pipe(
        catchError(() => of({ data: <GameRoutesInfo>{} })),
        map(res => {
          const gameURL = res?.data?.gameURL;
          menu.activeRoutePath = gameURL ? [parent, gameURL].filter(s => !!s).join('/') : '';
          return menu;
        }),
      );
    }

    menu.activeRoutePath = link;
    return of(menu);
  }

  /**
   * 對整個 menu 列表（含巢狀 `infoExpandList`）遞迴地批次解析美化 URL。
   * 每個 item 的自身與子項目並行處理，所有項目全部完成後才 emit 結果。
   *
   * @param items 要解析的 menu item 列表
   * @returns 所有 item（含子項目）皆已填入 `activeRoutePath` 的列表
   */
  private resolveAllFriendlyUrls<T extends { infoExpandList?: any[]; activeRoutePath: string }>(
    items: T[],
  ): Observable<T[]> {
    if (!items.length) return of([]);

    const requests = items.map(item => {
      const fetchParent$ = this.resolveFriendlyUrl(item);

      // 如果有 children，遞迴處理
      const fetchChildren$ = item?.infoExpandList?.length ? this.resolveAllFriendlyUrls(item?.infoExpandList) : of([]);

      // 當前 item 請求 + children 請求，同時並行
      return forkJoin([fetchParent$, fetchChildren$]).pipe(map(([updatedItem]) => updatedItem));
    });

    return forkJoin(requests);
  }

  upsertByIdent(list: any[], inserts: any[]) {
    const map = new Map(list.map(i => [i.ident, i]));
    for (const it of inserts) map.set(it.ident, { ...map.get(it.ident), ...it });
    const insertIdents = new Set(inserts.map(i => i.ident));
    const others = list.filter(i => !insertIdents.has(i.ident));
    return [...inserts.map(i => map.get(i.ident)!), ...others];
  }

  /** 手機選單關閉或換頁後，重置 dropdown 狀態；桌機 dropdown 不跟隨路由變更 */
  checkRouteToExpandMenu() {
    if (!this.leftMenu) return;

    const url = this.router.url.split('/').slice(2).join('/');
    if (url === '404') return; // 404頁面不反應，避免點擊 menu item 轉到 404 後快速開關

    if (this.isH5) this.collapseAllDropdowns();
  }

  private collapseAllDropdowns(): void {
    this.leftMenu.forEach(item => (item.expand = false));
    this.defaultMenuData.forEach(item => (item.expand = false));
  }

  //语言选择初始化
  langInit() {
    const langMenu = this.findMenuItem(this.defaultMenuData, ['langSelect']).langSelect;
    if (!langMenu) return;
    langMenu.options[0].select =
      this.allLangData.find(x => this.appService.languageCode === x.code.toLowerCase())?.id || this.allLangData[0]?.id;
    langMenu.options[0].options = this.allLangData.map(x => {
      return {
        text: x.name,
        menuIcon: this.countryUtils.getCountryFilePath(x.code),
        ...x,
      };
    });
  }

  //关闭菜单
  turn(v: boolean) {
    this.layout.leftMenuState$.next(v);
  }

  //检查顶部菜单路由是否激活
  checkIfHeaderRouterActive(page: HeaderMenu): boolean {
    const url = this.router.url.split('/').slice(2).join('/');
    const pageUrl = page.config?.assignUrl?.split('/').slice(1).join('/');
    const ifCurrentURLMatchAssignURL =
      url && pageUrl && page.redirectMethod === LabelScenesRedirectMethodEnum['AssignUrl'] && url.includes(pageUrl);

    return !!ifCurrentURLMatchAssignURL;
  }

  //点击顶部主要菜单，只處理跳轉；dropdown 展開狀態不跟隨導航變更
  clickMainMenu(item: any, isActive?: any) {
    if (isActive && this.checkIfHeaderRouterActive(item)) return;

    const url = `/${this.appService.languageCode}${this.miniGameService.getLinkByMethod(item)}`;
    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  // 这个只打开一层，放着备用
  toggleItem(target: any, ident: string) {
    target.forEach((x: any) => {
      x.expand = x.ident === ident;
      if (x.children) this.toggleItem(x.children, ident);
    });
  }

  // 这个打开符合的层和所有子层
  toggleItemInherit(target: any, ident: string, inherit?: boolean) {
    target.forEach((x: any) => {
      // if (inherit || x.ident === ident) x.expand = true; //不自动关闭
      x.expand = inherit === undefined ? x.ident === ident : inherit; //自动关闭
      if (x.children) this.toggleItemInherit(x.children, ident, x.ident === ident ? x.expand : undefined);
    });
  }

  //按ident寻找某一个菜单
  findMenuItem(data: any[], idents: string[]): any {
    const result: any = {};
    data.forEach(x => {
      if (idents.includes(x.ident)) {
        result[x.ident] = x;
      }
      if (x.children) {
        x.children.forEach((v: any) => {
          if (idents.includes(v.ident)) {
            result[v.ident] = v;
          }
        });
      } else if (x.options) {
        x.options.forEach((v: any) => {
          if (idents.includes(v.ident)) {
            result[v.ident] = v;
          }
        });
      }
    });
    return result;
  }

  //点击任何菜单项。主要是展开/收起的功能
  clickMenu(target: any, isRailItem = false) {
    this.menuSync$.next(false);

    if (target.isOtherPage) {
      window.open('https://pubssupport.zendesk.com/hc/en-gb', '_blank');
      return;
    }

    if (target.isCS) {
      this.appService.toOnLineService$.next(true);
      return;
    }

    if (target.isNotification) {
      this.notificationDialogService.openNotification(1);
      return;
    }

    if (target.isWallet) {
      this.walletService.openWallet();
      return;
    }

    if (target.isLogout) {
      this.appService.saveUrl();
      this.appService.logoutSubject$.next(true);
    }

    if (target.isChat) {
      // 檢查使用者有沒有登入
      if (!this.localStorageService.loginToken) {
        // 把 menu 收起來
        if (this.layout.leftMenuBackdrop$.value) {
          this.turn(false);
        }

        this.appService.jumpToRegister();
        return;
      }

      this.sendbirdChatService.toggleChat();
      // 利用左側選單狀態是否有遮罩來判斷要不要合起來，因為沒有地方可以判斷寬度介於1200 ~ 560之間
      if (this.layout.leftMenuBackdrop$.value) {
        this.turn(false);
      }
      return;
    }

    //存在子菜单，正常展开
    if (target.infoExpandList?.length > 0) {
      // 若為 Rail Menu，點擊後只會觸發 - 展開／收合
      if (isRailItem) {
        target.expand = !target.expand;
        return;
      }

      // 若為 Full Menu，需確認是否有項目連結，有 - 轉導+展開
      const linkPath = this.miniGameService.getLinkByMethod(target);
      if (linkPath && linkPath !== 'undefined') {
        // 等待路由動畫啟動，並延遲路由轉導，避免主執行序卡住，使用動畫停頓
        requestAnimationFrame(() =>
          setTimeout(() => this.router.navigateByUrl(`/${this.appService.languageCode}${linkPath}`), 100),
        );

        // Ｈ5時，點擊任何連結跳轉後，選單都應該收合並關閉，同時導向該頁面（若當頁面已經載入，則收合並關閉即可）
        if (this.isH5) this.turn(false);
      }

      // Full Menu 點擊 dropdown item 只展開自身，不收合其他 dropdown
      target.expand = true;
      return;
    }

    //点击子菜单 获取url跳转
    if (!target.infoExpandList?.length && !target.options && !target.special) {
      const url = `/${this.appService.languageCode}${this.miniGameService.getLinkByMethod(target)}`;
      this.router.navigateByUrl(url);

      //全屏游戏关闭菜单
      const isDropdownRedirect = target.redirectMethod === LabelScenesRedirectMethodEnum['DropDownList'];
      if (!isDropdownRedirect && url.includes('play')) this.turn(false);

      // Ｈ5時，點擊任何連結跳轉後，選單都應該收合並關閉，同時導向該頁面（若當頁面已經載入，則收合並關閉即可）
      if (this.isH5) this.turn(false);
      return;
    }

    //存在下拉展开菜单，交由下拉展开操作
    if (target.options) return;

    // 当点击需要关闭菜单
    if (target.closeOnclick) {
      this.turn(false);
      return;
    }

    // 特殊展开收起
    if (target.trigger) {
      target.value = target.value === 0 ? 1 : 0;
      this.clickTrigger(target);
      return;
    }

    //点击搜索游戏
    if (target.isSearG) {
      this.miniGameService.openSearchDialog();
      this.turn(false);
      return;
    }
  }

  //点击收起时候的菜单
  clickRailMenu(target: any, isHeaderItem?: boolean) {
    if (isHeaderItem) {
      const header = this.leftMenu.find((x: any) => x.labelId == target.labelId);
      this.clickMenu(header, true);
      this.clickMainMenu(header, false);
      return;
    }

    this.clickMenu(target, true);
    //存在下拉展开菜单，交由下拉展开操作
    if (target.options) return;
  }

  toggleDropdownItem(target: any): void {
    target.expand = !target.expand;
  }

  //长按菜单
  onPressMenu(target: any) {}

  //点击特殊触发器
  clickTrigger(target: any) {}

  //检查是否禁用
  checkDisabled(target: any): boolean {
    if (target.disabled) return true;
    if (!this.logined && target.needLogined) return true;
    return false;
  }

  //菜单项是选择列表时的点击选择项动作
  optionsSelect(options: any, listItem: any) {
    switch (options.ident) {
      // case 'oddsFormat':
      //   this.localStorageService.oddsType = listItem.id;
      //   this.appService.oddsType = listItem.id;
      //   this.appService.oddsType$.next(listItem.id);
      //   if (this.logined) {
      //     this.settingsApi.modifyOddsFormat({ oddsFormat: listItem.id }).subscribe(data => {
      //       if (data) {
      //         this.toast.show({ message: this.localeService.getValue('set_s'), type: 'success' });
      //       } else {
      //         this.toast.show({ message: this.localeService.getValue('set_f'), type: 'fail' });
      //       }
      //     })
      //   }
      //   options.optionsExpand = false;
      //   break;
      // case 'countrySelect':
      //   this.localStorageService.betMode = listItem.id;
      //   this.appService.betMode$.next(listItem.id);
      //   if (this.logined) {
      //     this.settingsApi.modifyViewFormat({ viewFormat: listItem.id }).subscribe(data => {
      //       if (data) {
      //         this.toast.show({ message: this.localeService.getValue('set_s'), type: 'success' });
      //       } else {
      //         this.toast.show({ message: this.localeService.getValue('set_f'), type: 'fail' });
      //       }
      //     })
      //   }
      //   options.optionsExpand = false;
      //   break;
      case 'langList':
        const targetCode = listItem.code.toLowerCase();
        if (this.appService.languageCode === targetCode) return;
        this.appService.selectLangFun(targetCode);
        options.optionsExpand = false;
        break;
      default:
        break;
    }
  }

  //延迟执行关闭(修复因为事件重复执行导致点击原文字无法关闭的bug)
  nextTick(optionsItem: any) {
    setTimeout(() => (optionsItem.optionsExpand = false), 0);
  }

  /**关闭tooltip  */
  closeTooltip(item: any) {
    if (Array.isArray(item)) {
      item.forEach(x => {
        this.closeTooltip(x);
      });
    } else {
      if (item.tooltip) item.tooltip = false;
      if (item.children) this.closeTooltip(item.children);
    }
  }
}
